import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import {
	SyncMortgagesInstructionsState,
	SyncMortgagesInstructionsStateModel,
} from './sync-mortgages-instructions.state';
import { SyncMortgagesPackagesSelectors } from '@state/business/mortgages-instructions/sync-mortgages-packages/sync-mortgages-packages.selectors';
import { CloserSyncTransaction } from '@models/mortgages/closer-sync-instruction';
import { MortgageTransactionStatus } from '@models/mortgages/mortgage-base';
import {
	SyncMortgagePackage,
	SyncMortgagePackageStatus,
} from '@models/mortgages/closer-sync-mortgage-packages';

@Injectable()
export class SyncMortgagesInstructionsSelectors {
	@Selector([SyncMortgagesInstructionsState])
	static getAllMortgagesInstructions(
		state: SyncMortgagesInstructionsStateModel,
	): CloserSyncTransaction[] {
		return state.data;
	}

	@Selector([SyncMortgagesInstructionsSelectors.getAllMortgagesInstructions])
	static getAllValidTransactions(
		transactions: CloserSyncTransaction[],
	): CloserSyncTransaction[] {
		return transactions.filter(
			(transaction: CloserSyncTransaction) => transaction.valid,
		);
	}

	@Selector([
		SyncMortgagesInstructionsSelectors.getAllValidTransactions,
		SyncMortgagesPackagesSelectors.getAllDeletedMortgagePackages,
	])
	static getUnassignedTransactions(
		transactions: CloserSyncTransaction[],
		deletedUploadedMortgagePackages: SyncMortgagePackage[],
	): CloserSyncTransaction[] {
		return transactions
			.filter(
				(transaction: CloserSyncTransaction) =>
					!deletedUploadedMortgagePackages
						.filter(
							(pkg: SyncMortgagePackage) =>
								pkg.instructionId === transaction.instructionId,
						)
						.sort((a, b) => b.uploadedAt - a.uploadedAt)[0]?.deletedAt,
			) //Remove mortgages instrucitons that have been soft deleted
			.filter(
				(transaction: CloserSyncTransaction) =>
					transaction.status === MortgageTransactionStatus.ACCEPTED &&
					(!(transaction?.matterId?.length > 0) ||
						transaction?.matterId === null),
			);
	}

	@Selector([SyncMortgagesInstructionsSelectors.getAllValidTransactions])
	static getAssignedTransactions(
		transactions: CloserSyncTransaction[],
	): CloserSyncTransaction[] {
		return transactions.filter(
			(transaction: CloserSyncTransaction) =>
				transaction.status === MortgageTransactionStatus.ACCEPTED &&
				transaction?.matterId?.length > 0,
		);
	}

	@Selector([SyncMortgagesInstructionsSelectors.getAllValidTransactions])
	static getAmendments(
		transactions: CloserSyncTransaction[],
	): CloserSyncTransaction[] {
		return transactions.filter(
			(transaction: CloserSyncTransaction) =>
				transaction.latestVersionId !== transaction.currentAssignedVersionId &&
				transaction.currentAssignedVersionId !== null,
		);
	}

	@Selector([SyncMortgagesInstructionsSelectors.getAllValidTransactions])
	static getFormValidations(
		transactions: CloserSyncTransaction[],
	): CloserSyncTransaction[] {
		return transactions.filter((transaction: CloserSyncTransaction) => {
			const validStatuses = [
				MortgageTransactionStatus.FINAL_REPORT_READY_FOR_REVIEW,
				MortgageTransactionStatus.REQUEST_FOR_FUNDS_READY_FOR_REVIEW,
			];
			return validStatuses.includes(transaction.status);
		});
	}
}
